import React, { useState, useEffect, forwardRef, useImperativeHandle, useMemo } from 'react';
import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  IconButton,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Tooltip,
  Switch,
  FormControlLabel,
  Snackbar,
  CircularProgress,
  SelectChangeEvent,
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Archive as ArchiveIcon,
  RestoreFromTrash as RestoreIcon,
  Telegram as TelegramIcon,
} from '@mui/icons-material';
import { tradersService, usersService, payoutsService } from '../../../services';
import { Trader, TraderGroup, TraderFormData, User, TraderPayoutOutLimits, RoleEnum } from '../../../types/index';
import TraderOutLimitsEnableDialog, { type TraderOutLimitsEnablePayload } from './TraderOutLimitsEnableDialog';
import { useNotification } from '../../../contexts/NotificationContext';
import AnimatedPage from '../../../components/AnimatedPage';
import { useSelector } from 'react-redux';
import { RootState } from '../../../store';
import { canPerformAction, ActionKey } from '../../../auth/actionMatrix';
import TelegramChatsSection from '@/components/TelegramChatsSection';
import { canCreateTraderTelegramChat, canEditTraderTelegramChat, hasExceptionalTraderTelegramCreateAccess } from '@/auth/traderTelegramAccess';
import type { TelegramChat } from '@/types/common/telegram';

interface EditTraderDialogProps {
  open: boolean;
  trader: Trader | null;
  groups: TraderGroup[];
  users: Pick<User, 'id' | 'username'>[];
  onClose: () => void;
  onSave: (traderId: string, data: Partial<TraderFormData>) => Promise<void>;
}

const EditTraderDialog: React.FC<EditTraderDialogProps> = ({ open, trader, groups, users, onClose, onSave }) => {
  const currentUser = useSelector((state: RootState) => state.auth.user);
  const chatsOnly = Boolean(trader && !canPerformAction(currentUser, ActionKey.TRADERS_UPDATE));
  const [telegramChats, setTelegramChats] = useState<TelegramChat[]>([]);
  const [telegramChatsLoading, setTelegramChatsLoading] = useState(false);
  const [telegramChatsError, setTelegramChatsError] = useState<string | null>(null);
  const [formData, setFormData] = useState<TraderFormData>({
    name: '',
    public_key: '',
    private_key: '',
    group_id: undefined,
    is_active: true,
    user_id: '',
    telegram_nickname: '',
  });
  const { showNotification } = useNotification();
  useEffect(() => {
    if (trader) {
      setFormData({
        name: trader.name,
        public_key: trader.public_key || '',
        private_key: trader.private_key || '',
        group_id: trader.group_id || undefined,
        is_active: trader.is_active,
        user_id: trader.user_id,
        telegram_nickname: trader.telegram_nickname || '',
      });
    } else {
      setFormData({
        name: '',
        public_key: '',
        private_key: '',
        group_id: undefined,
        is_active: true,
        user_id: '',
        telegram_nickname: '',
      });
    }
  }, [trader]);

  useEffect(() => {
    setTelegramChats([]);
    setTelegramChatsError(null);
    if (!open || !trader) {
      setTelegramChatsLoading(false);
      return;
    }

    let active = true;
    setTelegramChatsLoading(true);
    tradersService.getTelegramChats(trader.id)
      .then((data) => {
        if (active) setTelegramChats(data);
      })
      .catch(() => {
        if (active) setTelegramChatsError('Не удалось загрузить Telegram-группу');
      })
      .finally(() => {
        if (active) setTelegramChatsLoading(false);
      });

    return () => {
      active = false;
    };
  }, [open, trader]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (chatsOnly) return;
    if (!formData.user_id) {
      showNotification('Выберите пользователя', 'error');
      return;
    }
    if (!formData.telegram_nickname) {
      showNotification('Введите Telegram ник', 'error');
      return;
    }
    if (trader) {
      await onSave(trader.id, formData);
    } else {
      await onSave('', formData);
    }
    onClose();
  };

  const handleChange = (field: keyof TraderFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: TraderFormData) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSelectChange = (field: keyof TraderFormData) => (e: SelectChangeEvent) => {
    setFormData((prev: TraderFormData) => ({
      ...prev,
      [field]: e.target.value,
    }));
  };

  const handleSwitchChange = (field: keyof TraderFormData) => (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData((prev: TraderFormData) => ({
      ...prev,
      [field]: e.target.checked,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{chatsOnly ? `Telegram-группы: ${trader?.name}` : trader ? 'Редактировать трейдера' : 'Создать трейдера'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Stack spacing={2}>
            {!chatsOnly && <>
            <FormControl fullWidth disabled={Boolean(trader)}>
              <InputLabel>Пользователь</InputLabel>
              <Select
                value={formData.user_id}
                onChange={handleSelectChange('user_id')}
                label="Пользователь"
                required
              >
                {users.map((user) => (
                  <MenuItem key={user.id} value={user.id}>
                    {user.username}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <FormControl fullWidth>
              <InputLabel>Группа</InputLabel>
              <Select
                value={formData.group_id || ''}
                onChange={handleSelectChange('group_id')}
                label="Группа"
              >
                <MenuItem value="">Без группы</MenuItem>
                {groups.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            <TextField
              label="Telegram ник"
              value={formData.telegram_nickname}
              onChange={handleChange('telegram_nickname')}
              required
              fullWidth
            />
            <TextField
              label="Название"
              value={formData.name}
              onChange={handleChange('name')}
              required
              fullWidth
            />
            <TextField
              label="Публичный ключ"
              value={formData.public_key}
              onChange={handleChange('public_key')}
              fullWidth
            />
            <TextField
              label="Приватный ключ"
              value={formData.private_key}
              onChange={handleChange('private_key')}
              fullWidth
            />
            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={handleSwitchChange('is_active')}
                />
              }
              label="Активен"
            />
            </>}
            {trader && (
              <TelegramChatsSection
                chats={telegramChats}
                loading={telegramChatsLoading}
                error={telegramChatsError}
                emptyText="Telegram-группа для этого трейдера не создавалась"
                ownerType="TRADER"
                onCreate={canCreateTraderTelegramChat(currentUser) ? (payload) => tradersService.createTelegramChat(trader.id, payload) : undefined}
                onEdit={canEditTraderTelegramChat(currentUser) ? (chatId, payload) => tradersService.updateTelegramChat(trader.id, chatId, payload) : undefined}
                onChanged={(updated) => setTelegramChats((items) => items.some((item) => item.id === updated.id) ? items.map((item) => item.id === updated.id ? updated : item) : [...items, updated])}
              />
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>{chatsOnly ? 'Закрыть' : 'Отмена'}</Button>
          {!chatsOnly && <Button type="submit" variant="contained">
            Сохранить
          </Button>}
        </DialogActions>
      </form>
    </Dialog>
  );
};

interface DeleteTraderDialogProps {
  open: boolean;
  trader: Trader | null;
  isRestore?: boolean;
  onClose: () => void;
  onConfirm: () => Promise<void>;
}

const DeleteTraderDialog: React.FC<DeleteTraderDialogProps> = ({
  open,
  trader,
  isRestore = false,
  onClose,
  onConfirm,
}) => {
  const { showNotification } = useNotification();

  const handleConfirm = async () => {
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      console.error(isRestore ? 'Error restoring trader:' : 'Error deleting trader:', err);
      showNotification(
        isRestore ? 'Ошибка при восстановлении трейдера' : 'Ошибка при удалении трейдера',
        'error',
      );
    }
  };

  const handleClose = () => {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
    onClose();
  };

  if (!trader) return null;

  return (
    <Dialog 
      open={open} 
      onClose={handleClose} 
      maxWidth="sm" 
      fullWidth
      disableRestoreFocus
    >
      <DialogTitle>
        {isRestore ? 'Подтверждение восстановления' : 'Подтверждение удаления'}
      </DialogTitle>
      <DialogContent>
        <Stack spacing={2}>
          <Typography>
            {isRestore
              ? `Вы действительно хотите восстановить трейдера ${trader.name}?`
              : `Вы действительно хотите удалить трейдера ${trader.name}?`}
          </Typography>
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose}>Отмена</Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color={isRestore ? 'primary' : 'error'}
        >
          {isRestore ? 'Восстановить' : 'Удалить'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

interface TradersListRef {
  handleOpenDialog: () => void;
}

type TraderStatusFilter = 'all' | 'active' | 'inactive';

const TradersList = forwardRef<TradersListRef>((props, ref) => {
  const [traders, setTraders] = useState<Trader[]>([]);
  const [users, setUsers] = useState<Pick<User, 'id' | 'username'>[]>([]);
  const [groups, setGroups] = useState<TraderGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState<TraderStatusFilter>('all');
  const [payoutOutTogglingId, setPayoutOutTogglingId] = useState<string | null>(null);
  const [telegramOpeningId, setTelegramOpeningId] = useState<string | null>(null);
  /** Модалка лимитов при включении OUT; null — закрыта. */
  const [outLimitsEnableTrader, setOutLimitsEnableTrader] = useState<Trader | null>(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [selectedTrader, setSelectedTrader] = useState<Trader | null>(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success' as 'success' | 'error',
  });
  const [formData, setFormData] = useState<TraderFormData>({
    name: '',
    public_key: '',
    private_key: '',
    group_id: undefined,
    is_active: true,
    user_id: '',
    telegram_nickname: '',
  });
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [traderToDelete, setTraderToDelete] = useState<Trader | null>(null);
  /** false — активные трейдеры (is_archive=false), true — архив (is_archive=true). */
  const [isArchiveView, setIsArchiveView] = useState(false);
  const { showNotification } = useNotification();
  const currentUser = useSelector((state: RootState) => state.auth.user);

  const canCreate = useMemo(() => canPerformAction(currentUser, ActionKey.TRADERS_CREATE), [currentUser]);
  const canToggleActive = useMemo(() => canPerformAction(currentUser, ActionKey.TRADERS_TOGGLE_ACTIVE), [currentUser]);
  const canTogglePayoutOut = useMemo(
    () => canPerformAction(currentUser, ActionKey.TRADERS_PAYOUT_OUT_ENABLED),
    [currentUser],
  );
  const canUpdate = useMemo(() => canPerformAction(currentUser, ActionKey.TRADERS_UPDATE), [currentUser]);
  const canOpenEdit = canUpdate || hasExceptionalTraderTelegramCreateAccess(currentUser);
  const canDelete = useMemo(() => canPerformAction(currentUser, ActionKey.TRADERS_DELETE), [currentUser]);
  const needsEditorLookups = canCreate || canUpdate;

  const generateKey = () => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    const length = 32;
    return Array.from(crypto.getRandomValues(new Uint8Array(length)))
      .map((x) => chars[x % chars.length])
      .join('');
  };

  const fetchTraders = async (
    archive: boolean = isArchiveView,
    activeStatus: TraderStatusFilter = statusFilter,
  ) => {
    try {
      setLoading(true);
      const [tradersRaw, groupsData, usersData] = await Promise.all([
        tradersService.getTraders({
          is_archive: archive,
          is_active: activeStatus === 'all' ? undefined : activeStatus === 'active',
        }),
        needsEditorLookups ? tradersService.getTraderGroups() : Promise.resolve([]),
        needsEditorLookups
          ? currentUser?.role === RoleEnum.STAFF
            ? tradersService.getUserOptions()
            : usersService.getUsers()
          : Promise.resolve([]),
      ]);
      const tradersData = tradersRaw as Trader[];
      setTraders(tradersData);
      setGroups(groupsData);
      setUsers(usersData);

      setError(null);
    } catch (err: any) {
      console.error('Error fetching traders:', err);
      const errorMessage = err.response?.data?.detail || err.message || 'Ошибка при загрузке трейдеров';
      setError(errorMessage);
      showNotification(errorMessage, 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleCreateTrader = async (data: TraderFormData) => {
    try {
      const preparedData = {
        ...data,
        public_key: data.public_key || null,
        private_key: data.private_key || null,
        group_id: data.group_id || null,
        group_name: data.group_id ? groups.find(g => g.id === data.group_id)?.name || null : null,
      };

      await tradersService.createTrader(preparedData);
      await fetchTraders();
      showNotification('Трейдер успешно создан', 'success');
    } catch (err: any) {
      console.error('Error creating trader:', err);
      showNotification('Ошибка при создании трейдера', 'error');
    }
  };

  const handleEditTrader = async (traderId: string, data: Partial<TraderFormData>) => {
    try {
      const { user_id: _userId, ...editableData } = data;
      const preparedData = {
        ...editableData,
        public_key: editableData.public_key || null,
        private_key: editableData.private_key || null,
        group_id: editableData.group_id || null,
        group_name: editableData.group_id ? groups.find(g => g.id === editableData.group_id)?.name || null : null,
      };
      
      await tradersService.updateTrader(traderId, preparedData);
      await fetchTraders();
      showNotification('Трейдер успешно обновлен', 'success');
    } catch (err: any) {
      console.error('Error updating trader:', err);
      showNotification('Ошибка при обновлении трейдера', 'error');
    }
  };

  const handleSave = async (traderId: string, data: Partial<TraderFormData>) => {
    if (traderId) {
      await handleEditTrader(traderId, data);
    } else {
      await handleCreateTrader(data as TraderFormData);
    }
  };

  const handleToggleTraderIsDeled = async (traderId: string, restore: boolean) => {
    try {
      await tradersService.toggleTraderIsDeled(traderId);
      await fetchTraders();
      showNotification(
        restore ? 'Трейдер успешно восстановлен' : 'Трейдер успешно удалён',
        'success',
      );
    } catch (err: any) {
      console.error(restore ? 'Error restoring trader:' : 'Error deleting trader:', err);
      showNotification(
        restore ? 'Ошибка при восстановлении трейдера' : 'Ошибка при удалении трейдера',
        'error',
      );
    }
  };

  const handleOpen = (trader?: Trader) => {
    if (trader) {
      // Editing existing trader - check update permission
      if (!canOpenEdit) {
        showNotification('У вас нет прав для редактирования трейдеров', 'error');
        return;
      }
      setSelectedTrader(trader);
      setFormData({
        name: trader.name,
        public_key: trader.public_key || '',
        private_key: trader.private_key || '',
        group_id: trader.group_id || undefined,
        is_active: trader.is_active,
        user_id: trader.user_id,
        telegram_nickname: trader.telegram_nickname || '',
      });
    } else {
      // Creating new trader - check create permission
      if (!canCreate) {
        showNotification('У вас нет прав для создания трейдеров', 'error');
        return;
      }
      setSelectedTrader(null);
      setFormData({
        name: '',
        public_key: '',
        private_key: '',
        group_id: undefined,
        is_active: true,
        user_id: '',
        telegram_nickname: '',
      });
    }
    setEditDialogOpen(true);
  };

  const handleDeleteClick = (trader: Trader) => {
    setTraderToDelete(trader);
    setDeleteDialogOpen(true);
  };

  const handleOpenTelegramGroup = async (trader: Trader) => {
    setTelegramOpeningId(trader.id);

    try {
      const chats: TelegramChat[] = await tradersService.getTelegramChats(trader.id);
      const chatWithLink = chats.find((chat) => chat.invite_url);

      if (!chatWithLink?.invite_url) {
        showNotification('Для этого трейдера Telegram-группа не указана', 'error');
        return;
      }

      const telegramWindow = window.open(chatWithLink.invite_url, '_blank');

      if (!telegramWindow) {
        showNotification('Разрешите всплывающие окна, чтобы открыть Telegram-группу', 'error');
        return;
      }

      telegramWindow.opener = null;
    } catch (err) {
      console.error('Error opening trader Telegram group:', err);
      showNotification('Не удалось открыть Telegram-группу трейдера', 'error');
    } finally {
      setTelegramOpeningId(null);
    }
  };

  const handleDeleteConfirm = async () => {
    if (!traderToDelete) return;
    await handleToggleTraderIsDeled(traderToDelete.id, isArchiveView);
  };

  const handleConfirmOutLimitsEnable = async (payload: TraderOutLimitsEnablePayload) => {
    if (!outLimitsEnableTrader) return;
    const { id } = outLimitsEnableTrader;
    try {
      await tradersService.updateTrader(id, payload);
      const updated = await payoutsService.setTraderPayoutOutEnabled(id, { enabled: true });
      setTraders((prev) =>
        prev.map((item) =>
          item.id === id
            ? {
                ...item,
                payout_out_enabled: updated.payout_out_enabled,
                max_active_payouts: updated.max_active_payouts,
                daily_payout_count_limit: updated.daily_payout_count_limit,
                daily_payout_amount_limit: updated.daily_payout_amount_limit,
              }
            : item,
        ),
      );
      showNotification('Доступ к выплатам OUT включён', 'success');
      setOutLimitsEnableTrader(null);
    } catch (err: any) {
      console.error('Error enabling OUT with limits:', err);
      const detail = err.response?.data?.detail;
      const msg =
        (Array.isArray(detail) && detail[0]?.msg) ||
        (typeof detail === 'string' ? detail : null) ||
        err.message ||
        'Не удалось сохранить лимиты или включить доступ OUT';
      showNotification(typeof msg === 'string' ? msg : 'Ошибка', 'error');
      try {
        await fetchTraders();
      } catch {
        /* ignore */
      }
      throw err;
    }
  };

  useEffect(() => {
    fetchTraders(isArchiveView, statusFilter);
  }, [isArchiveView, statusFilter]);

  const handleArchiveToggle = () => {
    setIsArchiveView((prev) => !prev);
  };

  useImperativeHandle(ref, () => ({
    handleOpenDialog: () => handleOpen()
  }));

  const filteredTraders = useMemo(() => {
    const normalizedQuery = searchQuery.trim().toLowerCase();

    return traders.filter((trader) => {
      if (statusFilter === 'active' && !trader.is_active) {
        return false;
      }
      if (statusFilter === 'inactive' && trader.is_active) {
        return false;
      }
      if (normalizedQuery && !trader.name.toLowerCase().includes(normalizedQuery)) {
        return false;
      }
      return true;
    });
  }, [traders, searchQuery, statusFilter]);

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Box mt={2}>
        <Alert severity="error">{error}</Alert>
      </Box>
    );
  }

  return (
    <AnimatedPage>
      <Box>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h5">
            {isArchiveView ? 'Трейдеры — архив' : 'Трейдеры'}
          </Typography>
          <Box display="flex" alignItems="center" gap={2}>
            <Button
              variant={isArchiveView ? 'contained' : 'outlined'}
              startIcon={<ArchiveIcon />}
              onClick={handleArchiveToggle}
            >
              {isArchiveView ? 'Активные' : 'Архив'}
            </Button>
            <TextField
              size="small"
              placeholder="Поиск по имени"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              sx={{ minWidth: 260 }}
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel>Статус</InputLabel>
              <Select
                value={statusFilter}
                label="Статус"
                onChange={(e: SelectChangeEvent<TraderStatusFilter>) =>
                  setStatusFilter(e.target.value as TraderStatusFilter)
                }
              >
                <MenuItem value="all">Все</MenuItem>
                <MenuItem value="active">Активные</MenuItem>
                <MenuItem value="inactive">Неактивные</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </Box>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : (
          <TableContainer component={Paper}>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Название</TableCell>
                  <TableCell>Пользователь</TableCell>
                  <TableCell>Публичный ключ</TableCell>
                  <TableCell>Приватный ключ</TableCell>
                  <TableCell>Группа</TableCell>
                  <TableCell align="center">Статус</TableCell>
                  <TableCell align="center">Автоблок</TableCell>
                  <TableCell align="center">Выплаты OUT</TableCell>
                  <TableCell>Действия</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {filteredTraders.map((trader) => (
                  <TableRow key={trader.id}>
                    <TableCell>{trader.name}</TableCell>
                    <TableCell>{trader.username || users.find(u => u.id === trader.user_id)?.username || '-'}</TableCell>
                    <TableCell>{trader.public_key || '-'}</TableCell>
                    <TableCell>{trader.private_key || '-'}</TableCell>
                    <TableCell>{trader.group_name || 'Без группы'}</TableCell>
                    <TableCell align="center">
                      <Tooltip title={canToggleActive ? 'Изменить статус' : 'Недостаточно прав'}>
                        <span>
                          <Switch
                            checked={trader.is_active}
                            onChange={async (e) => {
                              if (!canToggleActive) {
                                showNotification('У вас нет прав для выполнения этой операции', 'error');
                                return;
                              }
                              try {
                                const updatedTrader = await tradersService.toggleTraderActive(trader.id);
                                setTraders((prev) =>
                                  prev.map((item) =>
                                    item.id === trader.id
                                      ? {
                                          ...item,
                                          is_active: updatedTrader?.is_active ?? !item.is_active,
                                        }
                                      : item,
                                  ),
                                );
                                showNotification(
                                  `Трейдер успешно ${updatedTrader?.is_active ? 'активирован' : 'деактивирован'}`,
                                  'success'
                                );
                              } catch (err) {
                                console.error('Error toggling trader status:', err);
                                showNotification('Ошибка при обновлении статуса трейдера', 'error');
                              }
                            }}
                            disabled={!canToggleActive}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          canToggleActive
                            ? (trader.is_autostop_enabled ? 'Исключить из автоблока' : 'Включить автоблок')
                            : 'Недостаточно прав'
                        }
                      >
                        <span>
                          <Switch
                            checked={trader.is_autostop_enabled}
                            onChange={async () => {
                              if (!canToggleActive) {
                                showNotification('У вас нет прав для выполнения этой операции', 'error');
                                return;
                              }
                              try {
                                const updatedTrader = await tradersService.toggleTraderAutostop(trader.id);
                                setTraders((prev) =>
                                  prev.map((item) =>
                                    item.id === trader.id
                                      ? {
                                          ...item,
                                          is_autostop_enabled:
                                            updatedTrader?.is_autostop_enabled ?? !item.is_autostop_enabled,
                                        }
                                      : item,
                                  ),
                                );
                                showNotification(
                                  `Автоблок для трейдера успешно ${updatedTrader?.is_autostop_enabled ? 'включен' : 'выключен'}`,
                                  'success'
                                );
                              } catch (err) {
                                console.error('Error toggling trader autostop:', err);
                                showNotification('Ошибка при обновлении состояния автоблока', 'error');
                              }
                            }}
                            disabled={!canToggleActive}
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          !canTogglePayoutOut
                            ? 'Только для администраторов (роль ADMIN или суперпользователь)'
                            : trader.payout_out_enabled
                              ? 'Трейдер видит пул исходящих выплат'
                              : 'Трейдер не видит пул исходящих выплат'
                        }
                      >
                        <span>
                          <Switch
                            checked={Boolean(trader.payout_out_enabled)}
                            onChange={async (e) => {
                              if (!canTogglePayoutOut) {
                                showNotification('У вас нет прав для выполнения этой операции', 'error');
                                return;
                              }
                              const nextEnabled = e.target.checked;
                              if (nextEnabled) {
                                setOutLimitsEnableTrader(trader);
                                return;
                              }
                              setPayoutOutTogglingId(trader.id);
                              try {
                                const updated: TraderPayoutOutLimits =
                                  await payoutsService.setTraderPayoutOutEnabled(trader.id, {
                                    enabled: false,
                                  });
                                setTraders((prev) =>
                                  prev.map((item) =>
                                    item.id === trader.id
                                      ? {
                                          ...item,
                                          payout_out_enabled: updated.payout_out_enabled,
                                          max_active_payouts: updated.max_active_payouts,
                                          daily_payout_count_limit: updated.daily_payout_count_limit,
                                          daily_payout_amount_limit: updated.daily_payout_amount_limit,
                                        }
                                      : item,
                                  ),
                                );
                                showNotification('Доступ к выплатам OUT выключен', 'success');
                              } catch (err: any) {
                                console.error('Error toggling payout OUT:', err);
                                const detail = err.response?.data?.detail;
                                const msg =
                                  (Array.isArray(detail) && detail[0]?.msg) ||
                                  (typeof detail === 'string' ? detail : null) ||
                                  err.message ||
                                  'Ошибка при обновлении доступа к выплатам OUT';
                                showNotification(msg, 'error');
                              } finally {
                                setPayoutOutTogglingId(null);
                              }
                            }}
                            disabled={
                              !canTogglePayoutOut ||
                              payoutOutTogglingId === trader.id ||
                              outLimitsEnableTrader?.id === trader.id
                            }
                          />
                        </span>
                      </Tooltip>
                    </TableCell>
                    <TableCell>
                      <Box sx={{ display: 'flex', gap: 1 }}>
                        <Tooltip title="Открыть Telegram-группу">
                          <span>
                            <IconButton
                              aria-label={`Открыть Telegram-группу трейдера ${trader.name}`}
                              onClick={() => void handleOpenTelegramGroup(trader)}
                              disabled={telegramOpeningId !== null}
                              color="primary"
                            >
                              {telegramOpeningId === trader.id ? (
                                <CircularProgress size={20} />
                              ) : (
                                <TelegramIcon />
                              )}
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={canUpdate ? 'Редактировать' : canOpenEdit ? 'Telegram-группы' : 'Недостаточно прав'}>
                          <span>
                            <IconButton 
                              onClick={() => handleOpen(trader)}
                              disabled={!canOpenEdit}
                              aria-label={canUpdate ? 'Редактировать трейдера' : 'Telegram-группы трейдера'}
                            >
                              <EditIcon />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip
                          title={
                            !canDelete
                              ? 'Недостаточно прав'
                              : isArchiveView
                                ? 'Восстановить'
                                : 'Удалить'
                          }
                        >
                          <span>
                            <IconButton
                              onClick={() => handleDeleteClick(trader)}
                              sx={{ color: isArchiveView ? 'primary.main' : '#d32f2f' }}
                              disabled={!canDelete}
                            >
                              {isArchiveView ? <RestoreIcon /> : <DeleteIcon />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}

        <EditTraderDialog
          open={editDialogOpen}
          trader={selectedTrader}
          groups={groups}
          users={users}
          onClose={() => setEditDialogOpen(false)}
          onSave={handleSave}
        />
        <DeleteTraderDialog
          open={deleteDialogOpen}
          trader={traderToDelete}
          isRestore={isArchiveView}
          onClose={() => setDeleteDialogOpen(false)}
          onConfirm={handleDeleteConfirm}
        />
        <TraderOutLimitsEnableDialog
          open={Boolean(outLimitsEnableTrader)}
          trader={outLimitsEnableTrader}
          onClose={() => setOutLimitsEnableTrader(null)}
          onConfirm={handleConfirmOutLimitsEnable}
        />
      </Box>
    </AnimatedPage>
  );
});

export default TradersList; 
